import { useEffect, useMemo, useState } from 'react';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorLockable from '~/components/editor/EditorLockable/EditorLockable';
import EditorNumber from '~/components/editor/EditorNumber/EditorNumber';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import EditorSlider from '~/components/editor/EditorSlider/EditorSlider';
import NumberInput from '~/components/widgets/NumberInput/NumberInput';
import {
  changeWaveformKind,
  waveformArea,
  waveformBounds,
  waveformDuration
} from '~/systems/experiment';
import { assert, assertNever, Bounds } from '~/util';
import { WaveformPreview } from '../WaveformPreview/WaveformPreview';

import type {
  Channel,
  Constraint,
  Device,
  KaiserWaveform,
  RampWaveform,
  Waveform
} from '~/models/studio';
import type { SelectOption } from '~/util';
import './WaveformProperties.css';

interface WaveformPropertiesProps {
  id: string;
  sequenceDuration: number;
  waveform: Waveform;
  isDetuning: boolean;
  device: Device;
  channel: Channel;
  onUpdate: (waveform: Waveform) => void;
  onCommit: () => void;
}

const wfKindOptions: SelectOption<Waveform['kind']>[] = [
  { label: 'Constant', value: 'constant' },
  { label: 'Ramp', value: 'ramp' },
  { label: 'Blackman', value: 'blackman' },
  { label: 'Interpolated', value: 'interpolated' },
  { label: 'Kaiser', value: 'kaiser' },
  { label: 'Custom', value: 'custom' }
];

const lockableLocks = ['duration', 'maxVal', 'area'] as const;

const SNAP_DISTANCE = 0.02;
const MAX_LINEAR_RANGE = 1000;

function getLinearDivs(bounds: Bounds, scale: number) {
  const range = bounds.max - bounds.min;
  assert(Number.isFinite(range));
  let tick = Math.pow(scale, Math.floor(Math.log(range) / Math.log(scale)));
  while (Number.isFinite(tick) && range / tick < 4) {
    tick /= 2;
  }
  const values: number[] = [];
  const start = Math.ceil(bounds.min / tick) * tick;
  const end = Math.floor(bounds.max / tick) * tick;
  for (let i = start; i <= end; i += tick) {
    values.push(i);
  }
  return values;
}

function getLogDivs(bounds: Bounds, scale: number) {
  const logMin = Math.log10(Math.max(bounds.min, 0.00001));
  const logMax = Math.log10(Math.min(bounds.max, 100000000));
  const orderMin = Math.floor(logMin);
  const orderMax = Math.ceil(logMax);
  const values: number[] = [];
  for (let order = orderMin; order < orderMax; order++) {
    const orderBounds = {
      min: Math.max(bounds.min, Math.pow(10, order)),
      max: Math.min(bounds.max, Math.pow(10, order + 1))
    };
    values.push(...getLinearDivs(orderBounds, scale));
  }
  return values;
}

function getAutoDivs(bounds: Bounds, scale: number) {
  const range = Math.abs(bounds.max - bounds.min);
  if (
    range > MAX_LINEAR_RANGE &&
    bounds.max !== 0 &&
    bounds.min !== 0 &&
    Math.sign(bounds.max) === Math.sign(bounds.min)
  ) {
    return { values: getLogDivs(bounds, scale), logarithmic: true };
  } else {
    return { values: getLinearDivs(bounds, scale), logarithmic: false };
  }
}

export default function WaveformProperties({
  id,
  sequenceDuration,
  waveform,
  isDetuning,
  device: _device,
  channel,
  onUpdate,
  onCommit
}: WaveformPropertiesProps) {
  const [sampleText, setSampleText] = useState(
    waveform.kind === 'custom' ? waveform.samples.join(', ') : ''
  );
  const [sampleError, setSampleError] = useState('');

  // Sync sampleText when waveform.kind becomes 'custom'
  useEffect(() => {
    if (waveform.kind === 'custom') {
      setSampleText(waveform.samples.join(', '));
      setSampleError('');
    }
  }, [waveform]);

  // Clear sampleError when sampleText becomes valid
  useEffect(() => {
    if (!sampleError) {
      return;
    }
    const tokens = sampleText.split(/[\s,;]+/).filter(Boolean);
    const invalid = tokens.filter((t) => !Number.isFinite(Number(t)));
    const nums = tokens.map(Number).filter((n) => Number.isFinite(n));
    if (invalid.length === 0 && nums.length >= 2) {
      setSampleError('');
    }
  }, [sampleText, sampleError]);

  function commitSamples() {
    if (waveform.kind !== 'custom') {
      return;
    }
    const tokens = sampleText.split(/[\s,;]+/).filter(Boolean);
    const nums = tokens.map(Number).filter((n) => Number.isFinite(n));
    const invalid = tokens.filter((t) => !Number.isFinite(Number(t)));
    if (invalid.length) {
      setSampleError(
        `Invalid ${invalid.length > 1 ? 'values' : 'value'}: ${invalid.join(', ')}`
      );
      return;
    }
    setSampleError('');
    onUpdate({ kind: 'custom', samples: nums });
    onCommit();
  }

  function updateRamp(wf: Partial<RampWaveform>) {
    assert(waveform.kind === 'ramp');
    onUpdate({ ...waveform, ...wf });
  }

  function updateKaiser(wf: Partial<KaiserWaveform>) {
    assert(waveform.kind === 'kaiser');
    onUpdate({ ...waveform, ...wf });
  }

  function updateDuration(duration: number) {
    assert('duration' in waveform);
    onUpdate({ ...waveform, duration });
  }

  function updateArray<T>(
    arr: readonly T[],
    idx: number,
    newVal: T
  ): readonly T[] {
    if (arr[idx] === newVal) {
      return arr;
    }
    const newArr = [...arr];
    newArr[idx] = newVal;
    return newArr;
  }

  function updateInterpolatedSample(index: number, vec: [number, number]) {
    assert(waveform.kind === 'interpolated');
    const times = updateArray(waveform.times, index, vec[0]) as number[];
    const values = updateArray(waveform.values, index, vec[1]) as number[];
    onUpdate({ ...waveform, values, times });
  }

  function removeSample(idx: number) {
    assert(waveform.kind === 'interpolated');
    if (waveform.times.length <= 2) {
      return;
    }
    const times = waveform.times.filter((_, i) => i !== idx);
    const values = waveform.values.filter((_, i) => i !== idx);
    times[0] = 0;
    times[times.length - 1] = 1;
    onUpdate({ ...waveform, values, times });
  }

  function addSample() {
    assert(waveform.kind === 'interpolated');
    const times = [...waveform.times, 1];
    const lastValue = waveform.values[waveform.values.length - 1] ?? 0;
    const values = [...waveform.values, lastValue];
    onUpdate({ ...waveform, values, times });
    onCommit();
  }

  function updateKind(kind: Waveform['kind']) {
    onUpdate(changeWaveformKind(waveform, kind));
    onCommit();
  }

  function updateConstraintKind(kind: Constraint['kind']) {
    assert('constraint' in waveform);
    switch (kind) {
      case 'maxVal':
        onUpdate({
          ...waveform,
          constraint: {
            kind,
            duration: waveformDuration(waveform),
            area: waveformArea(waveform)
          }
        });
        onCommit();
        break;
      case 'duration':
        onUpdate({
          ...waveform,
          constraint: {
            kind,
            maxVal: waveformBounds(waveform).max,
            area: waveformArea(waveform)
          }
        });
        break;
      case 'area':
        onUpdate({
          ...waveform,
          constraint: {
            kind,
            maxVal: waveformBounds(waveform).max,
            duration: waveformDuration(waveform)
          }
        });
        break;
      default:
        assertNever(kind);
    }
    onCommit();
  }

  function updateConstraintDuration(duration: number) {
    assert('constraint' in waveform);
    const constraint = waveform.constraint;
    assert('duration' in constraint);
    onUpdate({ ...waveform, constraint: { ...constraint, duration } });
  }

  function updateConstraintMaxVal(value: number) {
    const maxVal = parseFloat(value.toFixed(3));
    assert('constraint' in waveform);
    const constraint = waveform.constraint;
    assert('maxVal' in constraint);
    if (constraint.kind === 'duration') {
      let area = constraint.area;
      if ((maxVal < 0 && area > 0) || (maxVal > 0 && area < 0)) {
        area = -area;
      }
      onUpdate({
        ...waveform,
        constraint: { ...constraint, area, maxVal }
      });
      return;
    }
    onUpdate({ ...waveform, constraint: { ...constraint, maxVal } });
  }

  function updateConstraintArea(area: number) {
    assert('constraint' in waveform);
    const constraint = waveform.constraint;
    assert('area' in constraint);
    if (constraint.kind === 'duration') {
      let maxVal = constraint.maxVal;
      if ((area < 0 && maxVal > 0) || (area > 0 && maxVal < 0)) {
        maxVal = -maxVal;
      }
      onUpdate({
        ...waveform,
        constraint: { ...constraint, area, maxVal }
      });
      return;
    }
    onUpdate({ ...waveform, constraint: { ...constraint, area } });
  }

  const constraintValues = useMemo(() => {
    if (!('constraint' in waveform)) {
      return null;
    }
    return {
      duration: waveformDuration(waveform),
      maxVal: waveformBounds(waveform).max,
      area: waveformArea(waveform)
    };
  }, [waveform]);

  const limits = useMemo(() => {
    const wfDuration = waveformDuration(waveform);
    const otherWaveformsDuration = sequenceDuration - wfDuration;

    let durationLimit = {
      min: Math.max(0, channel.minDuration - otherWaveformsDuration),
      max: Math.max(0, (channel.maxDuration ?? 10000) - otherWaveformsDuration)
    };
    let valueLimit = isDetuning
      ? { min: -channel.maxAbsDetuning, max: channel.maxAbsDetuning }
      : { min: 0, max: channel.maxAmp };
    let areaLimit = { min: 0, max: (valueLimit.max - valueLimit.min) * 10 };

    switch (waveform.kind) {
      case 'interpolated':
      case 'ramp':
      case 'custom':
        break;
      case 'constant':
        areaLimit.min = valueLimit.min * durationLimit.min;
        break;
      case 'blackman':
      case 'kaiser': {
        const wf = waveform;
        switch (wf.constraint.kind) {
          case 'maxVal': {
            const { duration, area } = wf.constraint;
            if (area > 0 && valueLimit.max > 0) {
              const minDuration = waveformDuration({
                ...wf,
                constraint: { kind: 'duration', maxVal: valueLimit.max, area }
              });
              durationLimit.min = Math.max(durationLimit.min, minDuration);
            }
            if (area > 0 && valueLimit.min > 0) {
              const maxDuration = waveformDuration({
                ...wf,
                constraint: { kind: 'duration', maxVal: valueLimit.min, area }
              });
              durationLimit.max = Math.min(durationLimit.max, maxDuration);
            }
            const minArea = waveformArea({
              ...wf,
              constraint: { kind: 'area', maxVal: valueLimit.min, duration }
            });
            areaLimit.min = Math.max(areaLimit.min, minArea);
            const maxArea = waveformArea({
              ...wf,
              constraint: { kind: 'area', maxVal: valueLimit.max, duration }
            });
            areaLimit.max = Math.min(areaLimit.max, maxArea);
            break;
          }
          case 'duration': {
            const { maxVal, area } = wf.constraint;
            const minValue = waveformBounds({
              ...wf,
              constraint: { kind: 'maxVal', duration: durationLimit.max, area }
            }).max;
            valueLimit.min = Math.max(valueLimit.min, minValue);
            const maxValue = waveformBounds({
              ...wf,
              constraint: { kind: 'maxVal', duration: durationLimit.min, area }
            }).max;
            valueLimit.max = Math.min(valueLimit.max, maxValue);
            const minArea = waveformArea({
              ...wf,
              constraint: { kind: 'area', duration: durationLimit.min, maxVal }
            });
            areaLimit.min = Math.max(areaLimit.min, minArea);
            const maxArea = waveformArea({
              ...wf,
              constraint: { kind: 'area', duration: durationLimit.max, maxVal }
            });
            areaLimit.max = Math.min(areaLimit.max, maxArea);
            break;
          }
          case 'area': {
            const { maxVal, duration } = wf.constraint;
            const minValue = waveformBounds({
              ...wf,
              constraint: { kind: 'maxVal', area: areaLimit.min, duration }
            }).max;
            const maxValue = waveformBounds({
              ...wf,
              constraint: { kind: 'maxVal', area: areaLimit.max, duration }
            }).max;
            valueLimit.min = Math.max(valueLimit.min, minValue);
            valueLimit.max = Math.min(valueLimit.max, maxValue);
            if (maxVal !== 0) {
              const minDuration = waveformDuration({
                ...wf,
                constraint: { kind: 'duration', area: areaLimit.min, maxVal }
              });
              const maxDuration = waveformDuration({
                ...wf,
                constraint: { kind: 'duration', area: areaLimit.max, maxVal }
              });
              durationLimit.min = Math.max(durationLimit.min, minDuration);
              durationLimit.max = Math.min(durationLimit.max, maxDuration);
            }
            break;
          }
          default:
            assertNever(wf.constraint);
        }
        break;
      }
      default:
        assertNever(waveform);
    }

    valueLimit = Bounds.minBounded(valueLimit);
    areaLimit = Bounds.minBounded(areaLimit);
    durationLimit = Bounds.minBounded(durationLimit);

    return {
      duration: durationLimit,
      value: valueLimit,
      valuePi: { min: valueLimit.min / Math.PI, max: valueLimit.max / Math.PI },
      areaPi: { min: areaLimit.min / Math.PI, max: areaLimit.max / Math.PI }
    };
  }, [
    channel.maxAbsDetuning,
    channel.maxAmp,
    channel.maxDuration,
    channel.minDuration,
    isDetuning,
    sequenceDuration,
    waveform
  ]);

  const divisions = useMemo(
    () => ({
      duration: getAutoDivs(limits.duration, 10),
      valuePi: getAutoDivs(limits.valuePi, 2),
      areaPi: getLinearDivs(limits.areaPi, 2)
    }),
    [limits]
  );

  // Generate unique field names for UX and accessibility
  const generateFieldName = (label: string) => {
    return `${label}-${id}`;
  };

  return (
    <div className="WaveformProperties">
      <WaveformPreview
        waveform={waveform}
        isDetuning={isDetuning}
        basis={channel.basis}
      />
      <EditorSelect
        label="Waveform"
        name={generateFieldName('waveform')}
        value={waveform.kind}
        onChange={updateKind}
        options={wfKindOptions}
      />
      {'duration' in waveform && (
        <EditorSlider
          label="Duration"
          name={generateFieldName('duration')}
          value={waveform.duration}
          min={limits.duration.min}
          max={limits.duration.max}
          divisions={divisions.duration.values}
          snapRange={SNAP_DISTANCE}
          logarithmic={divisions.duration.logarithmic}
          step={1}
          unit="ns"
          onChange={updateDuration}
          onBlur={onCommit}
        />
      )}
      {'constraint' in waveform && (
        <>
          <EditorLockable
            locks={lockableLocks}
            currentLock={waveform.constraint.kind}
            onUpdateLock={(kind) =>
              updateConstraintKind(kind as Constraint['kind'])
            }
            editors={[
              <EditorSlider
                key="duration"
                label="Duration"
                name={generateFieldName('constraint-duration')}
                disabled={waveform.constraint.kind === 'duration'}
                value={constraintValues!.duration}
                min={limits.duration.min}
                max={limits.duration.max}
                divisions={divisions.duration.values}
                snapRange={SNAP_DISTANCE}
                logarithmic={divisions.duration.logarithmic}
                step={1}
                unit="ns"
                onChange={updateConstraintDuration}
                onBlur={onCommit}
              />,
              <EditorSlider
                key="maxVal"
                name={generateFieldName('constraint-max-val')}
                label={waveform.kind === 'constant' ? 'Value' : 'Max value'}
                disabled={waveform.constraint.kind === 'maxVal'}
                value={constraintValues!.maxVal / Math.PI}
                min={limits.valuePi.min}
                max={limits.valuePi.max}
                divisions={divisions.valuePi.values}
                snapRange={SNAP_DISTANCE}
                logarithmic={divisions.valuePi.logarithmic}
                step={0.1}
                unit="𝝅 rad/μs"
                onChange={(v) => updateConstraintMaxVal(v * Math.PI)}
                onBlur={onCommit}
              />,
              <EditorSlider
                key="area"
                label="Area"
                name={generateFieldName('constraint-area')}
                disabled={waveform.constraint.kind === 'area'}
                value={constraintValues!.area / Math.PI}
                min={limits.areaPi.min}
                max={limits.areaPi.max}
                divisions={divisions.areaPi}
                snapRange={SNAP_DISTANCE}
                step={0.1}
                unit="𝝅"
                onChange={(v) => updateConstraintArea(v * Math.PI)}
                onBlur={onCommit}
              />
            ]}
          />
          {waveform.kind === 'kaiser' && (
            <EditorNumber
              label="Beta"
              name={generateFieldName('kaiser-beta')}
              value={waveform.beta}
              min={0}
              max={100}
              onChange={(v) => updateKaiser({ beta: v })}
              onBlur={onCommit}
            />
          )}
        </>
      )}
      {!('constraint' in waveform) && waveform.kind === 'ramp' && (
        <>
          <EditorSlider
            label="Start"
            name={generateFieldName('ramp-start')}
            value={waveform.start / Math.PI}
            min={limits.valuePi.min}
            max={limits.valuePi.max}
            divisions={divisions.valuePi.values}
            snapRange={SNAP_DISTANCE}
            logarithmic={divisions.valuePi.logarithmic}
            step={0.1}
            unit="𝝅 rad/μs"
            onChange={(v) => updateRamp({ start: v * Math.PI })}
            onBlur={onCommit}
          />
          <EditorSlider
            label="Stop"
            name={generateFieldName('ramp-stop')}
            value={waveform.stop / Math.PI}
            min={limits.valuePi.min}
            max={limits.valuePi.max}
            divisions={divisions.valuePi.values}
            snapRange={SNAP_DISTANCE}
            logarithmic={divisions.valuePi.logarithmic}
            step={0.1}
            unit="𝝅 rad/μs"
            onChange={(v) => updateRamp({ stop: v * Math.PI })}
            onBlur={onCommit}
          />
        </>
      )}
      {waveform.kind === 'interpolated' && (
        <div className="WaveformProperties-samplesList">
          <span>Samples</span>
          {waveform.times.map((_, idx) => (
            <div key={idx} className="WaveformProperties-sampleRow">
              <EditorButton
                className="WaveformProperties-sampleRowDelete"
                disabled={waveform.times.length <= 2}
                onClick={() => removeSample(idx)}
              >
                -
              </EditorButton>
              <label className="WaveformProperties-sampleRowLabel">
                <span className="WaveformProperties-labelText">#{idx + 1}</span>
                <NumberInput
                  name={generateFieldName(`waveform-times-${idx}`)}
                  value={waveform.times[idx]}
                  min={waveform.times[idx - 1] ?? 0}
                  max={waveform.times[idx + 1] ?? 1}
                  step={0.1}
                  disabled={idx === 0 || idx === waveform.times.length - 1}
                  onChange={(v) =>
                    updateInterpolatedSample(idx, [v, waveform.values[idx]])
                  }
                  onBlur={onCommit}
                />
                <NumberInput
                  name={generateFieldName(`waveform-values-${idx}`)}
                  value={waveform.values[idx] / Math.PI}
                  min={limits.valuePi.min}
                  max={limits.valuePi.max}
                  step={0.1}
                  onChange={(v) =>
                    updateInterpolatedSample(idx, [
                      waveform.times[idx],
                      v * Math.PI
                    ])
                  }
                  onBlur={onCommit}
                />
              </label>
            </div>
          ))}
          <EditorButton onClick={addSample}>+ sample</EditorButton>
        </div>
      )}
      {waveform.kind === 'custom' && (
        <>
          <textarea
            className="WaveformProperties-editorCode"
            value={sampleText}
            rows={6}
            spellCheck={false}
            onChange={(e) => setSampleText(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
          />
          {sampleError && (
            <p className="WaveformProperties-errorText">{sampleError}</p>
          )}
          <EditorButton onClick={commitSamples}>Apply</EditorButton>
        </>
      )}
    </div>
  );
}
